import React, { useMemo, useState } from 'react';
import { Search, Minus, Plus, X, Package, ArrowLeft } from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import type { PosProduct } from './types';
import { formatUzs } from './saleTotals';
import { matches } from '../../lib/translit';

interface Props {
  products: PosProduct[];
  loading: boolean;
  /** productId → miqdor */
  cart: Record<number, number>;
  onStep: (productId: number, delta: number) => void;
  onSetQuantity: (productId: number, quantity: number) => void;
  stockWarning: { id: number; message: string } | null;
  onBack: () => void;
}

/**
 * Tovar tanlash — qidiruv va miqdor boshqaruvi.
 *
 * Miqdorni qo'lda kiritish ham qoldirilgan: sotuvchi 24 ta tovarni "+" ni
 * 24 marta bosib emas, raqam yozib qo'shadi.
 */
export const ProductPicker: React.FC<Props> = ({
  products,
  loading,
  cart,
  onStep,
  onSetQuantity,
  stockWarning,
  onBack,
}) => {
  const [search, setSearch] = useState('');

  // Qidiruv alifboga bog'liq emas: "jip" ham, "Жип" ham bir xil topadi
  // (lib/translit.ts). Ilgari `toLowerCase().includes()` edi — klaviatura
  // kirilchada bo'lsa lotincha nomlar umuman chiqmasdi.
  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    return products.filter((p) => matches(p.name, search));
  }, [products, search]);

  return (
    <div className="flex flex-col min-h-0 min-w-0">
      {/* Orqaga va qidiruv bitta qatorda — alohida sarlavha panel joyni
          bekorga egallardi, bo'lim nomi navigatsiyada ko'rinib turadi. */}
      <div className="page-bar flex items-center gap-2 mb-1">
        <button
          type="button"
          onClick={onBack}
          className="w-10 h-10 shrink-0 grid place-items-center rounded-lg text-gray-500 hover:bg-gray-100"
          aria-label="Orqaga"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-10 pr-10"
            placeholder="Tovar qidirish"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Qidiruvni tozalash"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title={search ? 'Hech narsa topilmadi' : 'Tovar yo\'q'}
          description={
            search
              ? 'Boshqa nom bilan qidirib ko\'ring.'
              : 'Avval Tovarlar bo\'limidan tovar qo\'shing.'
          }
        />
      ) : (
        <div className="space-y-2 overflow-y-auto min-h-0">
          {filtered.map((product) => {
            const quantity = cart[product.id] || 0;
            const inCart = quantity > 0;
            const warning = stockWarning?.id === product.id ? stockWarning.message : null;

            return (
              <div
                key={product.id}
                className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                  inCart ? 'border-primary-600 bg-primary-50' : 'border-gray-100 bg-white'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{product.name}</div>
                  <div className="flex items-center gap-3 text-sm mt-0.5">
                    <span className="text-gray-900 font-medium tabular-nums">
                      {formatUzs(product.sell_price_uzs)}
                    </span>
                    <span className={product.stock > 0 ? 'text-gray-500' : 'text-danger-600'}>
                      {product.stock} ta
                    </span>
                  </div>
                  {warning && <div className="text-xs text-danger-600 mt-1">{warning}</div>}
                </div>

                {inCart ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => onStep(product.id, -1)}
                      className="w-9 h-9 grid place-items-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      aria-label="Kamaytirish"
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => onSetQuantity(product.id, parseInt(e.target.value, 10) || 0)}
                      onFocus={(e) => e.currentTarget.select()}
                      className="w-14 h-9 text-center rounded-lg border border-gray-200 tabular-nums
                        focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label={`${product.name} miqdori`}
                    />
                    <button
                      type="button"
                      onClick={() => onStep(product.id, 1)}
                      className="w-9 h-9 grid place-items-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      aria-label="Ko'paytirish"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onStep(product.id, 1)}
                    disabled={product.stock <= 0}
                    className="w-9 h-9 grid place-items-center rounded-lg bg-primary-600 text-white shrink-0
                      hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label={`${product.name} qo'shish`}
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
